import React from 'react';

export const SITE_URL = 'https://milliesmotorhomehire.co.uk';
const BRAND = "Millie's Motorhome Hire";
const DEFAULT_IMAGE = 'https://customer-assets-v7afamib.emergentagent.net/job_39775d55-8769-4a7d-8ca7-65bbba50c556/artifacts/a663690b7c53036d_IMG_8046.jpeg';

// React 19 hoists <title>, <meta> and <link> rendered anywhere in the tree into <head>.
const SEO = ({
  title = BRAND,
  rawTitle = false,
  description = 'Family-run motorhome hire in Suffolk & Essex. Luxury 4, 5 & 6 berth Roller Team motorhomes from £120/night with insurance, unlimited mileage and free parking included. Book online.',
  keywords = 'motorhome hire Suffolk, motorhome hire Essex, campervan hire Suffolk, motorhome rental, Roller Team hire, 6 berth motorhome hire, pet friendly motorhome hire',
  image = DEFAULT_IMAGE,
  url,
  type = 'website',
  noindex = false,
  structuredData = null,
}) => {
  const fullUrl = url ? `${SITE_URL}${url}` : SITE_URL;
  const fullTitle = rawTitle || title === BRAND ? title : `${title} | ${BRAND}`;
  const schemas = structuredData ? (Array.isArray(structuredData) ? structuredData : [structuredData]) : [];

  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={fullUrl} />
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1'} />

      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={BRAND} />
      <meta property="og:locale" content="en_GB" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      <meta name="author" content={BRAND} />
      <meta name="geo.region" content="GB-SFK" />
      <meta name="geo.placename" content="Wattisham, Suffolk" />
      <meta name="geo.position" content="52.1266;0.9560" />
      <meta name="ICBM" content="52.1266, 0.9560" />

      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(s) }} />
      ))}
    </>
  );
};

const ADDRESS = {
  '@type': 'PostalAddress',
  streetAddress: 'Clay Hill Farm, Clay Hill Lane',
  addressLocality: 'Wattisham',
  addressRegion: 'Suffolk',
  postalCode: 'IP7 7JS',
  addressCountry: 'GB',
};

const AREA_SERVED = ['Suffolk', 'Essex', 'Ipswich', 'Bury St Edmunds', 'Sudbury', 'Felixstowe', 'Colchester', 'Chelmsford', 'Southend-on-Sea', 'Norfolk', 'Cambridgeshire']
  .map((name) => ({ '@type': 'AdministrativeArea', name }));

export const generateBusinessSchema = () => ({
  '@context': 'https://schema.org',
  '@type': ['LocalBusiness', 'AutoRental'],
  '@id': `${SITE_URL}/#business`,
  name: BRAND,
  description: 'Family-run motorhome hire in Suffolk serving Suffolk and Essex. Luxury 4, 5 and 6 berth Roller Team motorhomes with insurance, unlimited mileage and free parking included.',
  url: SITE_URL,
  logo: 'https://customer-assets.emergentagent.com/job_better-website-5/artifacts/01cb8dc0_Screenshot%202026-01-10%20185208.png',
  image: DEFAULT_IMAGE,
  telephone: '+447527006966',
  email: 'milliesmotorhomehire@gmail.com',
  address: ADDRESS,
  geo: { '@type': 'GeoCoordinates', latitude: 52.1266, longitude: 0.956 },
  areaServed: AREA_SERVED,
  openingHoursSpecification: {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    opens: '08:00',
    closes: '18:00',
  },
  priceRange: '££',
  currenciesAccepted: 'GBP',
  paymentAccepted: 'Credit Card, Debit Card',
  aggregateRating: { '@type': 'AggregateRating', ratingValue: '5.0', reviewCount: '26', bestRating: '5' },
  makesOffer: { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Motorhome hire', areaServed: AREA_SERVED }, priceCurrency: 'GBP', price: '120', priceSpecification: { '@type': 'UnitPriceSpecification', price: '120', priceCurrency: 'GBP', unitText: 'per night' } },
  sameAs: ['https://www.facebook.com/100432836071005', 'https://www.instagram.com/millies_motorhome_hire/'],
});

export const generateMotorhomeSchema = (motorhome) => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: motorhome.name,
  description: motorhome.description,
  image: motorhome.images,
  brand: { '@type': 'Brand', name: 'Roller Team' },
  category: 'Motorhome hire',
  offers: {
    '@type': 'Offer',
    priceCurrency: 'GBP',
    price: motorhome.pricePerNight,
    priceSpecification: { '@type': 'UnitPriceSpecification', price: motorhome.pricePerNight, priceCurrency: 'GBP', unitText: 'per night' },
    priceValidUntil: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    availability: motorhome.availableFrom ? 'https://schema.org/PreOrder' : 'https://schema.org/InStock',
    url: `${SITE_URL}/motorhomes/${motorhome.slug}`,
    seller: { '@id': `${SITE_URL}/#business` },
    areaServed: AREA_SERVED,
  },
  additionalProperty: [
    { '@type': 'PropertyValue', name: 'Berth', value: motorhome.berth },
    { '@type': 'PropertyValue', name: 'Seat belts', value: motorhome.seatBelts },
    { '@type': 'PropertyValue', name: 'Transmission', value: motorhome.specifications?.transmission },
    { '@type': 'PropertyValue', name: 'Length', value: motorhome.specifications?.length },
  ],
});

export const generateFAQSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: { '@type': 'Answer', text: faq.answer },
  })),
});

export const generateReviewSchema = (reviews) => ({
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': `${SITE_URL}/#business`,
  name: BRAND,
  aggregateRating: { '@type': 'AggregateRating', ratingValue: '5.0', reviewCount: String(Math.max(reviews.length, 26)) },
  review: reviews.map((review) => ({
    '@type': 'Review',
    author: { '@type': 'Person', name: review.author || review.name },
    datePublished: review.date,
    reviewRating: { '@type': 'Rating', ratingValue: String(review.rating) },
    reviewBody: review.text || review.content,
  })),
});

export const generateBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [{ name: 'Home', path: '/' }, ...items].map((item, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: item.name,
    item: `${SITE_URL}${item.path}`,
  })),
});

export const generateArticleSchema = (guide, path) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: guide.title,
  description: guide.metaDescription,
  image: guide.image,
  datePublished: guide.published,
  dateModified: guide.published,
  author: { '@type': 'Organization', name: BRAND, url: SITE_URL },
  publisher: { '@type': 'Organization', name: BRAND, url: SITE_URL },
  mainEntityOfPage: `${SITE_URL}${path}`,
});

export default SEO;
